import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  Eye,
  LogIn,
  RotateCcw,
  Sparkles,
  Sun,
  UserRound,
  Volume2,
  VolumeX,
  Zap,
} from "lucide-react";
import { KD9LogoCircle } from "@/components/KD9Logo";
import "@/components/EgyptEntryExperience.css";
import EgyptJourney from "@/components/EgyptJourney";

const FINAL_DESKTOP_ART = Object.freeze({
  src: "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/409c957424fc257d0b4330cfe00ac29c62687ad187c88ec5a4ed19e8c552f580.png",
  width: 2560,
  height: 1440,
});
const FINAL_DESKTOP_BG = FINAL_DESKTOP_ART.src;
const FINAL_MOBILE_ART = Object.freeze({
  src: "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/2a7de0a9897a36389e0aadfbbd7cb2bdc34f5fffe4e148febcf29a2256d66d67.png",
  width: 1080,
  height: 1920,
});
const FINAL_MOBILE_BG = FINAL_MOBILE_ART.src;
const FALLBACK_BG = FINAL_DESKTOP_BG;

const KD9_ART = {
  explorers:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/6a6353849bd027bf019fefc459548711f1ce26234fcc6a6302d7d7681e0723c6.png",
  diagnostics:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/38b812bf1b127538cf7bc8ffda861781098d6bb1e68e9318f0fe53a6306dc18e.png",
  academy:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/f1d02a1ed35d413ec4de34af9e9f226cd25b5fa431fd84deb2421c71c35cc1e4.png",
  temple:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/ec4c4e857d3c3e68538145d2277764d207bd035d8dbaf1019c43d78197daa294.png",
  support:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/3d00f205bfbb4740c5c207f51bda145bfd296713caab979f4cd16b43829a1527.png",
  market:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/5a941b5461e822aba153d7cb27033977347279f4548c1dae7c7bd5e4cd6c24d7.png",
  daily:
    "https://cdn.openart.ai/openart-uploads/production/attachment-transfers/1fe02a63ce05789443771a7321ef88211f09a357d9196c9d5e75b114adf1d7e8.png",
};

const LANDMARKS = [
  {
    key: "diagnostics",
    label: "DIAGNÓSTICOS",
    hint: "Pirámides del conocimiento",
    to: "/diagnosticos",
    className: "egypt-pyramids",
    left: "11%",
    top: "41%",
    avatarLeft: "24%",
    beamAngle: -29,
    delay: 0.12,
  },
  {
    key: "academy",
    label: "ACADEMIA KD9",
    hint: "Esfinge del aprendizaje",
    to: "/academia",
    className: "egypt-sphinx",
    left: "29%",
    top: "51%",
    avatarLeft: "36%",
    beamAngle: -18,
    delay: 0.28,
  },
  {
    key: "temple",
    label: "TEMPLO VIVO",
    hint: "Gran templo central",
    to: "/app/templo-vivo",
    className: "egypt-temple",
    left: "50%",
    top: "30%",
    avatarLeft: "50%",
    beamAngle: -3,
    delay: 0.44,
  },
  {
    key: "support",
    label: "ACOMPAÑAMIENTO",
    hint: "Santuario de encuentro",
    to: "/acompanamiento",
    className: "egypt-sanctuary",
    left: "70%",
    top: "49%",
    avatarLeft: "64%",
    beamAngle: 17,
    delay: 0.6,
  },
  {
    key: "market",
    label: "MARKETPLACE",
    hint: "Mercado del templo",
    to: "/tienda",
    className: "egypt-market",
    left: "88%",
    top: "43%",
    avatarLeft: "76%",
    beamAngle: 29,
    delay: 0.76,
  },
];

const DAILY_DESTINATION = {
  key: "daily",
  label: "ENERGÍA DIARIA",
  hint: "Horóscopo y energía del día",
  action: "daily",
  left: "50%",
  top: "48%",
  avatarLeft: "50%",
  beamAngle: -3,
};

const DESTINATIONS = [...LANDMARKS, DAILY_DESTINATION];

const DESTINATION_SOUND_PROFILES = {
  diagnostics: { type: "triangle", frequencies: [82.41, 123.47], duration: 1.05, stagger: 0.08, gain: 0.038 },
  academy: { type: "sine", frequencies: [196, 293.66, 392], duration: 0.92, stagger: 0.12, gain: 0.028 },
  temple: { type: "sine", frequencies: [65.41, 98, 130.81], duration: 1.3, stagger: 0.035, gain: 0.036 },
  support: { type: "sine", frequencies: [220, 277.18, 329.63], duration: 1.0, stagger: 0.09, gain: 0.024 },
  market: { type: "triangle", frequencies: [523.25, 659.25, 783.99], duration: 0.55, stagger: 0.1, gain: 0.022 },
  daily: { type: "sine", frequencies: [432, 648, 864], duration: 1.15, stagger: 0.14, gain: 0.024 },
};

const AvatarArtThumb = ({ variant }) => (
  <span className={`egypt-avatar-thumb ${variant === "woman" ? "is-woman" : "is-man"}`} aria-hidden="true">
    <img src={KD9_ART.explorers} alt="" loading="eager" decoding="async" />
  </span>
);

const AvatarCard = ({ active, icon, artVariant, title, subtitle, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group rounded-2xl border p-5 text-left transition-all hover:-translate-y-1"
    style={{
      borderColor: active ? "var(--kd9-gold)" : "var(--kd9-border)",
      background: active ? "rgba(212,175,55,.12)" : "rgba(5,7,12,.72)",
      boxShadow: active ? "0 0 30px rgba(212,175,55,.16)" : "none",
    }}
  >
    <div className="flex items-center gap-4">
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center"
        style={{ border: "1px solid var(--kd9-border)", background: "rgba(255,255,255,.03)" }}
      >
        {artVariant ? <AvatarArtThumb variant={artVariant} /> : icon}
      </div>
      <div>
        <div className="font-cinzel kd9-gold-text tracking-[.12em]">{title}</div>
        <div className="font-mont text-[10px] mt-1" style={{ color: "var(--kd9-text-soft)" }}>
          {subtitle}
        </div>
      </div>
    </div>
  </button>
);

const structureFor = (key) => (
  <img
    src={KD9_ART[key]}
    alt=""
    className="egypt-landmark-art"
    loading="eager"
    decoding="async"
    fetchPriority="low"
    draggable="false"
    aria-hidden="true"
  />
);


const EgyptEntryExperience = ({ config = {}, onExit, previewMode = false }) => {
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const sceneRef = useRef(null);
  const walkTimerRef = useRef(null);
  const touchStartXRef = useRef(null);
  const pointerFrameRef = useRef(null);
  const pointerPositionRef = useRef(null);
  const selectAvatar = config.show_avatar_selector !== false;
  const rememberedAvatar =
    !previewMode && typeof window !== "undefined" ? window.sessionStorage.getItem("kd9-egypt-avatar") : "";
  const rememberedIlluminated =
    !previewMode && typeof window !== "undefined" && window.sessionStorage.getItem("kd9-egypt-illuminated") === "1";
  const rememberedLandmark =
    !previewMode && typeof window !== "undefined" ? window.sessionStorage.getItem("kd9-egypt-landmark") : "";
  const validRememberedLandmark = DESTINATIONS.some((item) => item.key === rememberedLandmark)
    ? rememberedLandmark
    : "";
  const [avatar, setAvatar] = useState(
    selectAvatar && ["man", "woman"].includes(rememberedAvatar) ? rememberedAvatar : selectAvatar ? "" : "explorer"
  );
  const [illuminated, setIlluminated] = useState(rememberedIlluminated);
  const [awakening, setAwakening] = useState(false);
  const [selectedKey, setSelectedKey] = useState(validRememberedLandmark);
  const [walking, setWalking] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [arriving, setArriving] = useState(false);
  const [entering, setEntering] = useState(null);
  const [backgroundReady, setBackgroundReady] = useState(false);
  const lowPower = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const cores = navigator.hardwareConcurrency || 8;
    const memory = navigator.deviceMemory || 8;
    return cores <= 4 || memory <= 4;
  }, []);
  const illuminateTimerRef = useRef(null);
  const enterTimerRef = useRef(null);
  const arrivalTimerRef = useRef(null);
  const footstepIntervalRef = useRef(null);
  const destinationCueRef = useRef({ key: "", at: 0 });
  const audioEngineRef = useRef({ ctx: null, wind: null, fire: null, windGain: null, fireGain: null });

  const particles = useMemo(
    () =>
      Array.from({ length: reduceMotion ? 0 : lowPower ? 10 : 26 }, (_, index) => ({
        id: index,
        left: `${(index * 37) % 98}%`,
        top: `${12 + ((index * 29) % 74)}%`,
        delay: (index % 8) * 0.24,
        size: 1 + (index % 3),
      })),
    [lowPower, reduceMotion]
  );

  const createNoiseSource = useCallback((ctx, seconds = 2) => {
    const frameCount = Math.max(1, Math.floor(ctx.sampleRate * seconds));
    const buffer = ctx.createBuffer(1, frameCount, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < frameCount; index += 1) {
      data[index] = Math.random() * 2 - 1;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    return source;
  }, []);

  const stopAmbientAudio = useCallback(() => {
    if (footstepIntervalRef.current) {
      window.clearInterval(footstepIntervalRef.current);
      footstepIntervalRef.current = null;
    }
    const engine = audioEngineRef.current;
    for (const source of [engine.wind, engine.fire]) {
      if (source) {
        try { source.stop(); } catch {}
      }
    }
    if (engine.ctx && engine.ctx.state !== "closed") {
      engine.ctx.close().catch(() => {});
    }
    audioEngineRef.current = { ctx: null, wind: null, fire: null, windGain: null, fireGain: null };
  }, []);

  const startAmbientAudio = useCallback(async () => {
    if (config.ambient_sound === false || typeof window === "undefined") return;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    let engine = audioEngineRef.current;
    if (!engine.ctx || engine.ctx.state === "closed") {
      const ctx = new AudioContextClass();
      const wind = createNoiseSource(ctx, 3);
      const windFilter = ctx.createBiquadFilter();
      windFilter.type = "lowpass";
      windFilter.frequency.value = 720;
      const windGain = ctx.createGain();
      windGain.gain.value = 0.022;
      wind.connect(windFilter).connect(windGain).connect(ctx.destination);

      const fire = createNoiseSource(ctx, 1.4);
      const fireFilter = ctx.createBiquadFilter();
      fireFilter.type = "bandpass";
      fireFilter.frequency.value = 1850;
      fireFilter.Q.value = 0.8;
      const fireGain = ctx.createGain();
      fireGain.gain.value = illuminated ? 0.012 : 0.004;
      fire.connect(fireFilter).connect(fireGain).connect(ctx.destination);

      wind.start();
      fire.start();
      engine = { ctx, wind, fire, windGain, fireGain };
      audioEngineRef.current = engine;
    }
    if (engine.ctx.state === "suspended") await engine.ctx.resume();
    setSoundOn(true);
  }, [config.ambient_sound, createNoiseSource, illuminated]);

  const toggleSound = useCallback(async () => {
    if (soundOn) {
      stopAmbientAudio();
      setSoundOn(false);
      return;
    }
    await startAmbientAudio();
  }, [soundOn, startAmbientAudio, stopAmbientAudio]);

  const playFootstep = useCallback(() => {
    const ctx = audioEngineRef.current.ctx;
    if (!ctx || !soundOn || ctx.state !== "running") return;
    const duration = 0.11;
    const frameCount = Math.max(1, Math.floor(ctx.sampleRate * duration));
    const buffer = ctx.createBuffer(1, frameCount, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < frameCount; index += 1) {
      const fade = 1 - index / frameCount;
      data[index] = (Math.random() * 2 - 1) * fade;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 260;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.055, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
    source.connect(filter).connect(gain).connect(ctx.destination);
    source.start();
    source.stop(ctx.currentTime + duration);
  }, [soundOn]);

  const playDestinationCue = useCallback((key) => {
    const ctx = audioEngineRef.current.ctx;
    const profile = DESTINATION_SOUND_PROFILES[key];
    if (!ctx || !soundOn || ctx.state !== "running" || !profile) return;

    const nowMs = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (destinationCueRef.current.key === key && nowMs - destinationCueRef.current.at < 450) return;
    destinationCueRef.current = { key, at: nowMs };

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.9, ctx.currentTime);
    master.connect(ctx.destination);

    profile.frequencies.forEach((frequency, index) => {
      const start = ctx.currentTime + index * profile.stagger;
      const end = start + profile.duration;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = profile.type;
      oscillator.frequency.setValueAtTime(frequency, start);

      if (key === "academy" || key === "daily") {
        oscillator.frequency.exponentialRampToValueAtTime(frequency * 1.045, end);
      } else if (key === "temple" || key === "diagnostics") {
        oscillator.frequency.exponentialRampToValueAtTime(Math.max(40, frequency * 0.96), end);
      }

      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(profile.gain, start + 0.025);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      oscillator.connect(gain).connect(master);
      oscillator.start(start);
      oscillator.stop(end + 0.02);
    });

    const cleanupAt = ctx.currentTime + profile.duration + profile.stagger * profile.frequencies.length + 0.1;
    window.setTimeout(() => {
      try { master.disconnect(); } catch {}
    }, Math.max(100, (cleanupAt - ctx.currentTime) * 1000));
  }, [soundOn]);

  useEffect(
    () => () => {
      if (walkTimerRef.current) window.clearTimeout(walkTimerRef.current);
      if (illuminateTimerRef.current) window.clearTimeout(illuminateTimerRef.current);
      if (enterTimerRef.current) window.clearTimeout(enterTimerRef.current);
      if (arrivalTimerRef.current) window.clearTimeout(arrivalTimerRef.current);
      if (pointerFrameRef.current) window.cancelAnimationFrame(pointerFrameRef.current);
      stopAmbientAudio();
    },
    [stopAmbientAudio]
  );

  useEffect(() => {
    const engine = audioEngineRef.current;
    if (!soundOn || !engine.ctx || !engine.fireGain) return;
    const target = illuminated || awakening ? 0.013 : selectedKey ? 0.007 : 0.004;
    engine.fireGain.gain.setTargetAtTime(target, engine.ctx.currentTime, 0.3);
  }, [awakening, illuminated, selectedKey, soundOn]);

  useEffect(() => {
    if (!walking || !soundOn) {
      if (footstepIntervalRef.current) {
        window.clearInterval(footstepIntervalRef.current);
        footstepIntervalRef.current = null;
      }
      return undefined;
    }
    playFootstep();
    footstepIntervalRef.current = window.setInterval(playFootstep, 315);
    return () => {
      if (footstepIntervalRef.current) {
        window.clearInterval(footstepIntervalRef.current);
        footstepIntervalRef.current = null;
      }
    };
  }, [playFootstep, soundOn, walking]);

  const selected = DESTINATIONS.find((item) => item.key === selectedKey);
  const ready = Boolean(avatar);
  const background = config.background_image_url || FINAL_DESKTOP_BG;
  const mobileBackground =
    config.background_image_mobile_url || (config.background_image_url ? background : FINAL_MOBILE_BG);

  useEffect(() => {
    setBackgroundReady(false);
  }, [background, mobileBackground]);

  useEffect(() => {
    if (!ready || arriving || entering || !sceneRef.current) return;
    try {
      sceneRef.current.focus({ preventScroll: true });
    } catch {
      sceneRef.current.focus();
    }
  }, [arriving, entering, ready]);

  useEffect(() => {
    if (previewMode || typeof window === "undefined") return;
    window.sessionStorage.setItem("kd9-egypt-illuminated", illuminated ? "1" : "0");
  }, [illuminated, previewMode]);

  useEffect(() => {
    if (previewMode || typeof window === "undefined" || !selectedKey) return;
    window.sessionStorage.setItem("kd9-egypt-landmark", selectedKey);
  }, [previewMode, selectedKey]);

  const chooseAvatar = useCallback((choice) => {
    setAvatar(choice);
    if (!previewMode && typeof window !== "undefined") {
      window.sessionStorage.setItem("kd9-egypt-avatar", choice);
    }
    if (reduceMotion) {
      setArriving(false);
      return;
    }
    setArriving(true);
    if (arrivalTimerRef.current) window.clearTimeout(arrivalTimerRef.current);
    arrivalTimerRef.current = window.setTimeout(() => setArriving(false), 1450);
  }, [previewMode, reduceMotion]);

  const handlePointerMove = (event) => {
    if (!sceneRef.current || reduceMotion || lowPower || typeof window === "undefined") return;
    pointerPositionRef.current = { clientX: event.clientX, clientY: event.clientY };
    if (pointerFrameRef.current) return;
    pointerFrameRef.current = window.requestAnimationFrame(() => {
      pointerFrameRef.current = null;
      const scene = sceneRef.current;
      const pointer = pointerPositionRef.current;
      if (!scene || !pointer) return;
      const bounds = scene.getBoundingClientRect();
      const x = (pointer.clientX - bounds.left) / Math.max(bounds.width, 1) - 0.5;
      const y = (pointer.clientY - bounds.top) / Math.max(bounds.height, 1) - 0.5;
      scene.style.setProperty("--scene-shift-x", `${(x * 12).toFixed(2)}px`);
      scene.style.setProperty("--scene-shift-y", `${(y * 8).toFixed(2)}px`);
    });
  };

  const selectLandmark = (key) => {
    const next = DESTINATIONS.find((item) => item.key === key);
    if (!next || entering || awakening || arriving) return;
    setSelectedKey(key);
    playDestinationCue(key);
    if (!reduceMotion) {
      setWalking(true);
      if (walkTimerRef.current) window.clearTimeout(walkTimerRef.current);
      walkTimerRef.current = window.setTimeout(() => setWalking(false), 1050);
    }
  };

  const moveSelection = (direction) => {
    if (!ready || entering || awakening || arriving) return;
    const currentIndex = DESTINATIONS.findIndex((item) => item.key === selectedKey);
    const startIndex = currentIndex < 0 ? (direction > 0 ? -1 : 0) : currentIndex;
    const nextIndex = (startIndex + direction + DESTINATIONS.length) % DESTINATIONS.length;
    selectLandmark(DESTINATIONS[nextIndex].key);
  };

  const handleKeyDown = (event) => {
    if (!ready || entering || awakening || arriving) return;
    const target = event.target;
    const interactive =
      target !== event.currentTarget &&
      (target?.isContentEditable ||
        ["BUTTON", "A", "INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName) ||
        target?.closest?.("[role='button']"));
    if (interactive) return;

    if (event.key === "ArrowRight") {
      event.preventDefault();
      moveSelection(1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      moveSelection(-1);
    } else if (event.key === "Enter") {
      event.preventDefault();
    } else if (event.key === "Escape") {
      setSelectedKey("");
    }
  };

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.changedTouches?.[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    const startX = touchStartXRef.current;
    const endX = event.changedTouches?.[0]?.clientX;
    touchStartXRef.current = null;
    if (startX == null || endX == null) return;
    const interactive = event.target?.closest?.("button, a, input, textarea, select, [role='button']");
    if (interactive) return;
    const delta = endX - startX;
    if (Math.abs(delta) < 46) return;
    moveSelection(delta < 0 ? 1 : -1);
  };

  const handleIlluminate = () => {
    if (awakening || arriving || entering) return;
    if (illuminated || reduceMotion) {
      setIlluminated((value) => !value);
      setAwakening(false);
      return;
    }
    setSelectedKey("");
    setAwakening(true);
    if (illuminateTimerRef.current) window.clearTimeout(illuminateTimerRef.current);
    illuminateTimerRef.current = window.setTimeout(() => {
      setIlluminated(true);
      setAwakening(false);
    }, 2450);
  };

  const handleEnter = useCallback((destination) => {
    if (!destination || entering) return;
    const openDestination = () => {
      stopAmbientAudio();
      if (destination.action === "daily") {
        onExit?.({ target: "horoscopo-diario", persist: false });
        return;
      }
      navigate(destination.to);
    };
    if (reduceMotion) {
      openDestination();
      return;
    }
    setEntering(destination);
    setWalking(false);
    if (enterTimerRef.current) window.clearTimeout(enterTimerRef.current);
    enterTimerRef.current = window.setTimeout(openDestination, 1050);
  }, [entering, navigate, onExit, reduceMotion, stopAmbientAudio]);

  const resetPreview = useCallback(() => {
    if (!previewMode) return;
    if (walkTimerRef.current) window.clearTimeout(walkTimerRef.current);
    if (illuminateTimerRef.current) window.clearTimeout(illuminateTimerRef.current);
    if (enterTimerRef.current) window.clearTimeout(enterTimerRef.current);
    if (arrivalTimerRef.current) window.clearTimeout(arrivalTimerRef.current);
    stopAmbientAudio();
    setSoundOn(false);
    setEntering(null);
    setAwakening(false);
    setArriving(false);
    setWalking(false);
    setIlluminated(false);
    setSelectedKey("");
    setAvatar(selectAvatar ? "" : "explorer");
  }, [previewMode, selectAvatar, stopAmbientAudio]);

  return (
    <main
      className={`egypt-experience ${illuminated ? "is-illuminated" : "is-exploring"} ${awakening ? "is-awakening" : ""} ${arriving ? "is-arriving" : ""} ${entering ? "is-entering" : ""} ${lowPower ? "is-low-power" : ""}`}
      data-testid="egypt-entry-experience"
      ref={sceneRef}
      style={{
        "--entry-x": entering?.left || "50%",
        "--entry-y": entering?.top || "45%",
        "--lantern-focus-x": selected?.left || "50%",
        "--lantern-focus-y": selected?.top || "58%",
      }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onPointerMove={handlePointerMove}
      onPointerLeave={() => {
        if (pointerFrameRef.current) {
          window.cancelAnimationFrame(pointerFrameRef.current);
          pointerFrameRef.current = null;
        }
        pointerPositionRef.current = null;
        if (sceneRef.current) {
          sceneRef.current.style.setProperty("--scene-shift-x", "0px");
          sceneRef.current.style.setProperty("--scene-shift-y", "0px");
        }
      }}
    >
      <div className="egypt-sky-layer">
        <picture className="egypt-background-picture">
          <source
            media="(max-width: 580px)"
            srcSet={mobileBackground}
            width={FINAL_MOBILE_ART.width}
            height={FINAL_MOBILE_ART.height}
            data-kd9-mobile-art="1080x1920"
          />
          <img
            src={background}
            width={FINAL_DESKTOP_ART.width}
            height={FINAL_DESKTOP_ART.height}
            data-kd9-desktop-art="2560x1440"
            alt=""
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className={`egypt-background-image ${backgroundReady ? "is-ready" : ""}`}
            onLoad={() => setBackgroundReady(true)}
            onError={(event) => {
              if (event.currentTarget.src !== FALLBACK_BG) {
                event.currentTarget.src = FALLBACK_BG;
                return;
              }
              event.currentTarget.style.display = "none";
              setBackgroundReady(true);
            }}
          />
        </picture>
        <div className={`egypt-scene-loader ${backgroundReady ? "is-ready" : ""}`} aria-hidden="true">
          <span className="egypt-scene-loader-glyph">𓂀</span>
          <span>Preparando territorio</span>
        </div>
        <div className="egypt-sky-vignette" />
        <div className="egypt-moon" />
        <div className="egypt-stars" />
      </div>

      <div className="egypt-dune dune-back" />
      <div className="egypt-dune dune-mid" />
      <div className="egypt-dune dune-front" />
      <div className="egypt-wind-layer wind-far" aria-hidden="true" />
      <div className="egypt-wind-layer wind-near" aria-hidden="true" />

      <div className="egypt-entry-transition" aria-live="polite" aria-hidden={!entering}>
        <div className="egypt-entry-ring" />
        <div className="egypt-entry-copy">
          <span className="egypt-entry-kicker">ENTRANDO A</span>
          <span className="egypt-entry-title">{entering?.label || "KD9"}</span>
          <span className="egypt-entry-hint">{entering?.hint || "La energía abre el camino"}</span>
        </div>
      </div>

      <div className="egypt-arrival-transition" aria-live="polite" aria-hidden={!arriving}>
        <span className="egypt-arrival-light" />
        <div className="egypt-arrival-copy">
          <span className="egypt-arrival-kicker">EXPLORADOR KD9</span>
          <span className="egypt-arrival-title">TU LUZ DESPIERTA</span>
        </div>
      </div>

      {particles.map((p) => (
        <motion.span
          key={p.id}
          className="egypt-particle"
          style={{ left: p.left, top: p.top, width: p.size, height: p.size }}
          animate={reduceMotion ? undefined : { y: [0, -10, 0], opacity: [0.16, 0.72, 0.16] }}
          transition={{ duration: 4 + (p.id % 5), delay: p.delay, repeat: Infinity }}
        />
      ))}

      <div className="relative z-30 min-h-screen flex flex-col">
        <header className="flex items-center justify-between gap-4 px-5 md:px-10 py-5">
          <div className="flex items-center gap-3">
            <KD9LogoCircle size={50} />
            <div>
              <div className="font-cinzel text-sm md:text-base tracking-[.18em] kd9-gold-text">KHAOS DELTA 9</div>
              <div className="font-mont text-[9px] md:text-[10px] tracking-[.18em] uppercase text-white/55">
                Territorio ancestral
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {previewMode && (
              <>
                <span className="egypt-preview-badge">VISTA PREVIA</span>
                <button
                  type="button"
                  onClick={resetPreview}
                  className="kd9-btn-outline px-3 md:px-4 py-2 rounded-xl text-[10px] inline-flex items-center gap-2"
                  data-testid="egypt-preview-reset"
                >
                  <RotateCcw size={13} /> Reiniciar recorrido
                </button>
              </>
            )}
            {config.allow_skip !== false && (
              <button
                type="button"
                onClick={() => onExit?.()}
                className="kd9-btn-outline px-3 md:px-4 py-2 rounded-xl text-[10px]"
                data-testid="egypt-skip"
              >
                Saltar recorrido
              </button>
            )}
          </div>
        </header>

        {!ready ? (
          <section className="flex-1 flex items-center justify-center px-5 pb-10">
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full max-w-2xl rounded-3xl border p-6 md:p-9 backdrop-blur-md"
              style={{
                borderColor: "rgba(212,175,55,.32)",
                background: "rgba(3,5,9,.78)",
                boxShadow: "0 24px 80px rgba(0,0,0,.48)",
              }}
            >
              <div className="text-center mb-7">
                <div className="text-[10px] font-mont uppercase tracking-[.38em] text-white/45 mb-3">
                  Elige tu explorador
                </div>
                <h1 className="font-cinzel text-3xl md:text-5xl kd9-gold-text">ENTRA AL TERRITORIO</h1>
                <p className="font-cormorant text-xl md:text-2xl mt-3 text-white/70">
                  La energía que despierta lo ancestral.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <AvatarCard
                  active={avatar === "man"}
                  artVariant="man"
                  title="EXPLORADOR"
                  subtitle="Avatar masculino"
                  icon={<UserRound size={24} style={{ color: "var(--kd9-gold)" }} />}
                  onClick={() => chooseAvatar("man")}
                  testId="egypt-avatar-man"
                />
                <AvatarCard
                  active={avatar === "woman"}
                  artVariant="woman"
                  title="EXPLORADORA"
                  subtitle="Avatar femenino"
                  icon={<Sparkles size={24} style={{ color: "var(--kd9-gold)" }} />}
                  onClick={() => chooseAvatar("woman")}
                  testId="egypt-avatar-woman"
                />
              </div>
            </motion.div>
          </section>
        ) : (
          <section className="egypt-territory-wrap">
            <div className="egypt-territory-heading">
              <div className="font-mont text-[9px] md:text-[10px] tracking-[.34em] uppercase text-white/45 mb-2">
                Explora · Descubre · Ingresa
              </div>
              <h1 className="font-cinzel text-3xl md:text-5xl lg:text-6xl kd9-gold-text">EL TERRITORIO KD9</h1>
              <p className="font-cormorant text-base md:text-xl text-white/60 mt-2">
                Toca una estructura. Tu explorador caminará hacia ella.
              </p>
              <div className="egypt-control-hint" aria-hidden="true">
                <span className="egypt-control-desktop">← → explorar · usa el botón ENTRAR</span>
                <span className="egypt-control-mobile">Desliza para explorar</span>
              </div>
            </div>

            <div className="egypt-world" aria-label="Mapa interactivo del territorio KD9">
              <div className="egypt-explore-darkness" aria-hidden="true" />
              <div className="egypt-awakening-wave" aria-hidden="true" />
              <div className="egypt-awakening-copy" aria-live="polite">
                <span className="egypt-awakening-kicker">KHAOS DELTA 9</span>
                <span className="egypt-awakening-title">EL 9 DESPIERTA</span>
              </div>
              <svg className="egypt-energy-map" viewBox="0 0 1000 560" preserveAspectRatio="none" aria-hidden="true">
                <path className="energy-path path-left" d="M500 500 C420 430 300 380 120 290" />
                <path className="energy-path path-left-mid" d="M500 500 C450 420 390 350 300 330" />
                <path className="energy-path path-center" d="M500 500 C500 420 500 330 500 220" />
                <path className="energy-path path-right-mid" d="M500 500 C555 420 630 350 700 320" />
                <path className="energy-path path-right" d="M500 500 C600 430 740 370 880 285" />
                <text className="energy-nine" x="500" y="330" textAnchor="middle">9</text>
              </svg>

              <button
                type="button"
                className={`egypt-obelisk-main egypt-daily-obelisk ${selectedKey === "daily" ? "is-selected" : ""}`}
                onClick={() => selectLandmark("daily")}
                aria-label="Apuntar a energía diaria y horóscopo"
                aria-pressed={selectedKey === "daily"}
                data-testid="egypt-daily-energy"
              >
                <img
                  src={KD9_ART.daily}
                  alt=""
                  className="egypt-daily-art"
                  loading="eager"
                  decoding="async"
                  draggable="false"
                  aria-hidden="true"
                />
                <span className="egypt-daily-hitbox" aria-hidden="true" />
                <span className="obelisk-daily-label">ENERGÍA DIARIA</span>
              </button>

              {LANDMARKS.map((item) => (
                <button
                  type="button"
                  key={item.key}
                  className={`egypt-landmark ${item.className} ${selectedKey === item.key ? "is-selected" : ""}`}
                  style={{ left: item.left, top: item.top, "--ignite-delay": `${item.delay}s` }}
                  onClick={() => selectLandmark(item.key)}
                  data-testid={`egypt-landmark-${item.key}`}
                  aria-pressed={selectedKey === item.key}
                >
                  {structureFor(item.key)}
                  <span className="egypt-landmark-copy">
                    <span className="egypt-landmark-hint">{item.hint}</span>
                    <span className="egypt-landmark-label">{item.label}</span>
                  </span>
                </button>
              ))}

              <motion.div
                className={`egypt-avatar ${avatar === "woman" ? "avatar-woman" : "avatar-man"} ${walking ? "is-walking" : ""}`}
                animate={reduceMotion ? undefined : { left: selected?.avatarLeft || "50%" }}
                transition={{ duration: walking ? 0.95 : 0.45, ease: [0.22, 0.76, 0.25, 1] }}
                aria-label={avatar === "woman" ? "Exploradora KD9" : "Explorador KD9"}
              >
                <span className={`egypt-avatar-art-window ${avatar === "woman" ? "is-woman" : "is-man"}`} aria-hidden="true">
                  <img src={KD9_ART.explorers} alt="" loading="eager" decoding="async" draggable="false" />
                </span>
                <motion.span
                  className="avatar-flashlight-beam"
                  aria-hidden="true"
                  animate={
                    reduceMotion
                      ? undefined
                      : {
                          rotate: selected?.beamAngle ?? -7,
                          opacity: illuminated ? 0 : 1,
                        }
                  }
                  transition={{ duration: 0.62, ease: [0.22, 0.76, 0.25, 1] }}
                >
                  <span className="avatar-flashlight-core" />
                </motion.span>

              </motion.div>

              <div className="egypt-pathway" aria-hidden="true" />
              <div className="egypt-foreground-shadow" aria-hidden="true" />
            </div>

            <div className="egypt-action-dock">
              <div className="egypt-selected-panel" aria-live="polite">
                {selected ? (
                  <>
                    <div>
                      <div className="egypt-selected-kicker">La luz está apuntando a</div>
                      <div className="egypt-selected-title">{selected.label}</div>
                      <div className="egypt-selected-desc">Entra al destino señalado por la linterna.</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleEnter(selected)}
                      disabled={Boolean(entering)}
                      className="kd9-btn-gold px-5 py-3 rounded-xl text-[10px] text-center disabled:opacity-70"
                    >
                      {entering ? "ENTRANDO…" : `ENTRAR A ${selected.label}`}
                    </button>
                  </>
                ) : (
                  <div>
                    <div className="egypt-selected-kicker">Territorio en espera</div>
                    <div className="egypt-selected-title">ELIGE UNA ESTRUCTURA</div>
                    <div className="egypt-selected-desc">El explorador caminará hacia el destino.</div>
                  </div>
                )}
              </div>

              <div className="egypt-action-buttons">
                {config.show_illuminate !== false && (
                  <button
                    type="button"
                    onClick={handleIlluminate}
                    disabled={awakening}
                    className="kd9-btn-gold px-5 py-3 rounded-xl text-[10px] inline-flex items-center gap-2 disabled:opacity-70 disabled:cursor-wait egypt-primary-control"
                    data-testid="egypt-illuminate"
                  >
                    {illuminated ? <Eye size={14} /> : <Zap size={14} />}
                    {awakening ? "DESPERTANDO…" : illuminated ? "VOLVER A EXPLORAR" : "ILUMINAR"}
                  </button>
                )}
                <details className="egypt-more-menu">
                  <summary className="egypt-more-trigger">MENÚ</summary>
                  <div className="egypt-more-popover">
                    {config.ambient_sound !== false && (
                      <button
                        type="button"
                        onClick={toggleSound}
                        aria-pressed={soundOn}
                        className="egypt-menu-action"
                        data-testid="egypt-sound"
                      >
                        {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
                        {soundOn ? "SONIDO ON" : "SONIDO"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleEnter({
                        key: "portal",
                        label: "PORTAL KD9",
                        hint: "Acceso al ecosistema",
                        to: "/login",
                        left: "50%",
                        top: "30%",
                      })}
                      disabled={Boolean(entering)}
                      className="egypt-menu-action disabled:opacity-70"
                      data-testid="egypt-login"
                    >
                      <LogIn size={14} /> ENTRAR A KD9
                    </button>
                    <button
                      type="button"
                      onClick={() => onExit?.()}
                      className="egypt-menu-action"
                      data-testid="egypt-classic"
                    >
                      <Sun size={13} /> PORTADA CLÁSICA
                    </button>
                  </div>
                </details>
              </div>
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

// Keep the previous edition available to the Director as a reversible visual fallback.
export { EgyptEntryExperience as EgyptEntryLegacy };
const EgyptEntryRouter = (props) => props.config?.renderer === "legacy"
  ? <EgyptEntryExperience {...props} />
  : <EgyptJourney {...props} />;
export default EgyptEntryRouter;
